import { useCallback, useEffect, useState } from 'react';

/** Prefix of the localStorage key that remembers a sheet's width, in CSS pixels. */
export const SHEET_WIDTH_KEY_PREFIX = 'wb.sheetWidth.';
/** Narrowest sheet, in CSS pixels. */
export const MIN_SHEET_WIDTH = 280;
/** Space the sheet leaves free across the viewport: the 0.75rem gutters on both sides. */
export const SHEET_VIEWPORT_GUTTER = 24;
/** Width used when the default CSS width cannot be read (34rem at 16px). */
export const FALLBACK_SHEET_WIDTH = 16 * 34;

/** localStorage key of the sheet width for `storageKey`. */
export function sheetWidthKey(storageKey: string): string {
  return `${SHEET_WIDTH_KEY_PREFIX}${storageKey}`;
}

function rootFontSize(): number {
  try {
    const size = Number.parseFloat(getComputedStyle(document.documentElement).fontSize);
    return Number.isFinite(size) && size > 0 ? size : 16;
  } catch {
    return 16;
  }
}

/**
 * A CSS width (`width` prop of a sheet) in pixels: `Nrem` is N × the root font size, `Npx` is N, anything else is
 * the 34rem fallback. `fontSize` defaults to the document's root font size (16 when unknown).
 */
export function parseSheetWidth(css: string, fontSize: number = rootFontSize()): number {
  const m = /^\s*(\d+(?:\.\d+)?|\.\d+)(rem|px)\s*$/i.exec(css);
  if (!m) return FALLBACK_SHEET_WIDTH;
  const n = Number(m[1]);
  if (!(n > 0)) return FALLBACK_SHEET_WIDTH;
  return Math.round(m[2]?.toLowerCase() === 'rem' ? n * fontSize : n);
}

/** Widest sheet for a viewport `viewportWidth` px wide; an unknown width (0) gives no upper bound. */
export function maxSheetWidth(viewportWidth: number): number {
  if (!(viewportWidth > 0)) return Number.POSITIVE_INFINITY;
  return Math.max(MIN_SHEET_WIDTH, Math.floor(viewportWidth - SHEET_VIEWPORT_GUTTER));
}

/** `width` rounded and clamped to [280, viewport − 24]; a non-finite width becomes `fallback`. */
export function clampSheetWidth(width: number, viewportWidth = 0, fallback = FALLBACK_SHEET_WIDTH): number {
  const w = Number.isFinite(width) ? width : fallback;
  return Math.round(Math.min(maxSheetWidth(viewportWidth), Math.max(MIN_SHEET_WIDTH, w)));
}

/** The stored width for `storageKey`, clamped; `defaultWidth` when nothing usable is stored or storage is blocked. */
export function readSheetWidth(storageKey: string, defaultWidth: number, viewportWidth = 0): number {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(sheetWidthKey(storageKey));
  } catch {
    // storage unavailable: use the default
  }
  const n = stored === null || stored.trim() === '' ? Number.NaN : Number(stored);
  return clampSheetWidth(Number.isFinite(n) ? n : defaultWidth, viewportWidth, defaultWidth);
}

function store(storageKey: string, width: number | undefined): void {
  try {
    if (width === undefined) localStorage.removeItem(sheetWidthKey(storageKey));
    else localStorage.setItem(sheetWidthKey(storageKey), String(width));
  } catch {
    // storage unavailable: keep in memory only
  }
}

function viewportWidth(): number {
  return typeof window === 'undefined' ? 0 : window.innerWidth || 0;
}

/** Width state of a resizable sheet; `width` is always clamped to the current viewport. */
export interface SheetWidth {
  width: number;
  min: number;
  /** `Infinity` while the viewport width is unknown. */
  max: number;
  /** Sets the width (clamped); `persist: false` while a drag is in progress, so only the final width is stored. */
  set: (next: number, options?: { persist?: boolean }) => void;
  /** Back to the default width; forgets the stored choice. */
  reset: () => void;
}

export interface UseSheetWidthOptions {
  /** Remembers the width under `wb.sheetWidth.<storageKey>`; sheets sharing a key share a width. */
  storageKey: string;
  /** The sheet's default CSS width (`34rem`, `480px`). */
  defaultWidth: string;
  /**
   * Whether the sheet is shown (default true). Each time it opens the remembered width is read again, so a width
   * chosen in another sheet with the same key applies.
   */
  open?: boolean;
}

/**
 * Width of a right-side sheet (`Drawer`, `SidePanel`), persisted per browser in localStorage. The upper bound is the
 * viewport minus the gutters, tracked on window resizes.
 */
export function useSheetWidth({ storageKey, defaultWidth, open = true }: UseSheetWidthOptions): SheetWidth {
  const fallback = parseSheetWidth(defaultWidth);
  const [viewport, setViewport] = useState(viewportWidth);
  const [chosen, setChosen] = useState<{ key: string; fallback: number; open: boolean; width: number }>(
    () => ({
      key: storageKey,
      fallback,
      open,
      width: readSheetWidth(storageKey, fallback),
    }),
  );
  // A different key or default, or a reopen, re-reads what the key remembers (state update during render).
  const needsRead = chosen.key !== storageKey || chosen.fallback !== fallback || (open && !chosen.open);
  const width = needsRead ? readSheetWidth(storageKey, fallback) : chosen.width;
  if (needsRead || chosen.open !== open) setChosen({ key: storageKey, fallback, open, width });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const measure = () => setViewport(viewportWidth());
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const set = useCallback(
    (next: number, options: { persist?: boolean } = {}) => {
      const w = clampSheetWidth(next, viewport, fallback);
      setChosen({ key: storageKey, fallback, open, width: w });
      if (options.persist !== false) store(storageKey, w);
    },
    [viewport, fallback, storageKey, open],
  );
  const reset = useCallback(() => {
    setChosen({ key: storageKey, fallback, open, width: fallback });
    store(storageKey, undefined);
  }, [storageKey, fallback, open]);

  return {
    width: clampSheetWidth(width, viewport, fallback),
    min: MIN_SHEET_WIDTH,
    max: maxSheetWidth(viewport),
    set,
    reset,
  };
}
